import { useI18next } from "gatsby-plugin-react-i18next";
import { trim, last } from "lodash";
import { useServiceTree } from "./useServiceTree";
import { useUseCases } from "./useUseCases";

const linkMap = {
  method: {
    it: "metodo",
    en: "method",
    de: "methode",
  },
  work: {
    it: "lavora-con-noi",
    en: "work-with-us",
    de: "arbeiten-sie-mit-uns",
  },
  about: {
    it: "chi-siamo",
    en: "about-us",
    de: "uber-uns",
  },
  services: {
    it: "servizi",
    en: "services",
    de: "dienstleistungen",
  },
  caseStudy: {
    it: "caso-studio",
    en: "case-study",
    de: "fallstudie",
  },
};

const getSlugForLang = (path: string, lang: string) => {
  const entries = Object.entries(linkMap);

  for (const [, value] of entries) {
    if (Object.values(value).includes(path)) {
      return value[lang];
    }
  }

  return undefined;
};

export const useI18nLink = () => {
  const { language } = useI18next();
  const serviceMap = useServiceTree();
  const useCase = useUseCases();

  const services = [
    ...Object.values(serviceMap),
    ...Object.values(serviceMap).flatMap((s: any) => s.solutions || []),
  ];

  return (
    route: string,
    lang: string = language,
    fallback: boolean = true,
    anchor?: string,
  ) => {
    const path = last(trim(route, "/").split("/"))!;
    const sanitizedRoute = `/${route.replace(/(^\/*|\/*$)/g, "")}/`;

    const res = getSlugForLang(path, lang);

    if (res) return anchor ? `/${res}#${anchor}` : `/${res}/`;

    const service = services.find((s) =>
      [s.enSlug, s.deSlug, s.slug].includes(path),
    );
    if (service) {
      const slug = {
        it: `${linkMap.services.it}/${service.slug}`,
        en: `${linkMap.services.en}/${service.enSlug}`,
        de: `${linkMap.services.de}/${service.deSlug}`,
      }[lang];
      return anchor ? `/${slug}#${anchor}` : `/${slug}/`;
    }

    const useCaseItem = useCase.find((useCase) =>
      [useCase.enSlug, useCase.deSlug, useCase.slug].includes(path),
    );
    if (useCaseItem) {
      const slug = {
        it: `${linkMap.caseStudy.it}/${useCaseItem.slug}`,
        en: `${linkMap.caseStudy.en}/${useCaseItem.enSlug}`,
        de: `${linkMap.caseStudy.de}/${useCaseItem.deSlug}`,
      }[lang];
      return anchor ? `/${slug}#${anchor}` : `/${slug}/`;
    }

    if (route.includes("/blog/")) return `/blog`;

    if (fallback) return sanitizedRoute;

    return undefined;
  };
};

export const useServiceI18nLink = () => {
  const { language } = useI18next();

  const fn = (
    destination: { slug: string; enSlug: string; deSlug: string },
    lang: string = language,
  ) => {
    const route = {
      it: `${linkMap.services.it}/${destination.slug}`,
      en: `${linkMap.services.en}/${destination.enSlug}`,
      de: `${linkMap.services.de}/${destination.deSlug}`,
    }[lang];
    return `/${route}/`;
  };

  return {
    serviceLink: (
      destination: { slug: string; enSlug: string; deSlug: string },
      lang: string = language,
    ) => fn(destination, lang),
  };
};

export const useUseCaseI18nLink = () => {
  const { language } = useI18next();

  const fn = (
    destination: { slug: string; enSlug: string; deSlug: string },
    lang: string = language,
  ) => {
    const route = {
      it: `${linkMap.caseStudy.it}/${destination.slug}`,
      en: `${linkMap.caseStudy.en}/${destination.enSlug}`,
      de: `${linkMap.caseStudy.de}/${destination.deSlug}`,
    }[lang];
    return `/${route}/`;
  };

  return {
    useCaseLink: (
      destination: { slug: string; enSlug: string; deSlug: string },
      lang: string = language,
    ) => fn(destination, lang),
  };
};
